import React, { useEffect, useState } from 'react';
import { IbanElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { IconInfoCircle } from '@tabler/icons-react';
import styled from 'styled-components';
import { margin } from 'polished';
import { ClipLoader } from 'react-spinners';

import { getBillingDetails } from 'pages/Settings/SettingsPage/SettingsPayment/utils';

import { TotemButton } from 'ui/Button';

import { colors } from 'styles/theme';
import { TotemInput } from 'styles';

import { analyticsEvents, analyticsPropertiesConstants, analyticsSourceConstants, track } from 'tracking/segment';
import { CreditCardFormProps } from 'pages/Totem/Aside/Billing/CreditCardForm';
import { useOrganizationBillingInfo } from 'data/hooks/useOrganizationBillingInfo';
import { useCreateSetupIntentMutation, useSetStripeDefaultPaymentMethodMutation } from 'data/__generated__';

export const IBANForm: React.FC<CreditCardFormProps> = ({ organization, currentUser, onSubmitCompletion }) => {
    // inspired by https://github.com/stripe/react-stripe-js/blob/9fe1a5473cd1125fcda4e01adb6d6242a9bae731/examples/hooks/4-IBAN.js
    const stripe = useStripe();
    const elements = useElements();
    const [isLoading, setIsLoading] = useState(false);
    const [setStripeDefaultPaymentMethod] = useSetStripeDefaultPaymentMethodMutation();
    const [createSetupIntent] = useCreateSetupIntentMutation();
    const { billingInfo } = useOrganizationBillingInfo();
    const [cardName, setCardName] = useState<string | undefined | null>(billingInfo?.invoiceName);

    // La raison sociale vient de la requête dédiée aux infos de facturation, qui peut résoudre après le
    // premier rendu : on ne pré-remplit que si le champ n'a pas encore été renseigné.
    useEffect(() => {
        setCardName((currentCardName) => currentCardName ?? billingInfo?.invoiceName);
    }, [billingInfo?.invoiceName]);
    const [errorMessage, setErrorMessage] = useState<string | undefined | null>(null);
    const ICON_SIZE = 20;
    const ELEMENT_OPTIONS = {
        supportedCountries: ['SEPA'],
        hideIcon: true,
        style: {
            base: {
                fontSize: '18px',
                color: colors.black,
                letterSpacing: '0.025em',
                '::placeholder': {
                    color: colors.pantinGrey,
                },
            },
            invalid: {
                color: colors.red,
            },
        },
    };
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        setIsLoading(true);
        event.preventDefault();

        // In e2e, Stripe is mocked server-side (no real SetupIntent) and Stripe.js cannot confirm a
        // mocked client secret. Skip the Stripe Elements flow and register a default payment method
        // directly — same env.E2E bypass pattern used for Intercom (see App.tsx / AccountMenu).
        if (window.__TOTENV__.E2E) {
            if (currentUser?._id) {
                await setStripeDefaultPaymentMethod({
                    variables: {
                        userId: currentUser._id,
                        organizationId: organization._id,
                        paymentMethodId: 'pm_e2e_default',
                    },
                });
                onSubmitCompletion();
            }
            setIsLoading(false);
            return;
        }

        if (!stripe || !elements) {
            setIsLoading(false);
            return;
        }

        const ibanElement = elements.getElement(IbanElement);
        track(analyticsEvents.ADD_IBAN, {
            [analyticsPropertiesConstants.COMMON.SOURCE]: analyticsSourceConstants.CHECKOUT_BASKET,
        });
        const data = await createSetupIntent({
            variables: {
                organizationId: organization._id,
            },
        });

        const clientSecretObj = data?.data?.createSetupIntent;
        const clientSecret =
            typeof clientSecretObj === 'string' ? clientSecretObj : (clientSecretObj?.clientSecret ?? '');
        if (!ibanElement) {
            setErrorMessage('IBAN information is incomplete or invalid.');
            setIsLoading(false);
            return;
        }
        const { setupIntent, error } = await stripe.confirmSepaDebitSetup(clientSecret, {
            payment_method: {
                // type: STRIPE_SEPA_DEBIT,
                sepa_debit: ibanElement,
                billing_details: getBillingDetails({ cardName, billingInfo, user: currentUser }),
            },
        });
        if (error) {
            setErrorMessage(error?.message);
            setIsLoading(false);
            return;
        } else if (setupIntent.status === 'succeeded') {
            if (!currentUser?._id || !setupIntent?.payment_method) {
                // Sortie muette auparavant : le bouton restait bloqué en chargement, sans message ni
                // moyen de réessayer, alors que Stripe a bien enregistré le moyen de paiement.
                setErrorMessage(
                    "Le moyen de paiement a bien été enregistré mais n'a pas pu être sélectionné. Rechargez la page.",
                );
                setIsLoading(false);
                return;
            }
            const paymentMethodId =
                typeof setupIntent.payment_method === 'string'
                    ? setupIntent.payment_method
                    : setupIntent.payment_method?.id;
            await setStripeDefaultPaymentMethod({
                variables: {
                    userId: currentUser?._id,
                    organizationId: organization._id,
                    paymentMethodId,
                },
            });
            onSubmitCompletion();
        }
        setIsLoading(false);
    };
    return (
        <IBANFormContainer onSubmit={handleSubmit}>
            <CardNameInput
                placeholder="Titulaire du compte"
                value={cardName ?? ''}
                onChange={(e) => setCardName(e.target.value)}
            />
            <IBANContainer $margins={[1, 0, 0, 1]}>
                <IbanElement options={ELEMENT_OPTIONS} />
            </IBANContainer>
            <IBANDescription>
                <IconInfoCircle size={ICON_SIZE} />
                <div>
                    En enregistrant cet IBAN, vous acceptez les conditions d'utilisation du prélèvement{' '}
                    <a
                        href="https://s3.eu-central-1.amazonaws.com/totems3/assets/mandate_sepa.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        SEPA Direct Debit Stripe
                    </a>
                </div>
            </IBANDescription>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <TotemButton
                disabled={isLoading}
                $margins={[1, 0, 0, 0]}
                $fontSize="1em"
                type="submit"
                data-test="choose-iban"
            >
                {isLoading ? <ClipLoader color="white" size="16px" /> : "Enregistrer l'IBAN"}
            </TotemButton>
        </IBANFormContainer>
    );
};

const IBANFormContainer = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 1em 0 0 0;
`;
type IBANContainerProps = {
    $margins?: number[];
};
const IBANContainer = styled.div<IBANContainerProps>`
    ${({ $margins }) =>
        $margins &&
        margin(
            $margins[0] + 'rem',
            ($margins[1] ?? $margins[0]) + 'rem',
            ($margins[2] ?? $margins[0]) + 'rem',
            ($margins[3] ?? $margins[1] ?? $margins[0]) + 'rem',
        )}
    width: 100%;
`;

const IBANDescription = styled.div`
    margin-top: 2em;
    display: flex;
    width: 100%;
    svg {
        flex: 1 0 auto;
        margin-right: 0.5em;
    }
    a {
        text-decoration: underline;
    }
`;

const ErrorMessage = styled.div`
    margin: 1em 1em 0 1em;
    color: ${({ theme: { colors } }) => colors.red};
`;

const CardNameInput = styled(TotemInput)`
    border: none;
    margin: 0;
    &:hover {
        border: none;
    }
    &:active,
    &:focus {
        border: none;
        box-shadow: none;
    }
    color: #111111;
    font-family: Aeonik;
    font-size: 16px;
    outline: none;
`;
